import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useAppSelector } from '../../../../../../../app/hooks';
import { getTimeStamp } from '../../../../../../../utils/dateUtils';
import { deleteDoc } from '../../../../../../../common/MagicUpload/MagicUploadHelper';
import { FINANCIALS_FILE_TYPE } from '../../../financialsIngestion/constants';
import { DELETE_SUCCESS_MESSAGE } from './constants';
import { ProvisionalFileItem } from './types';

/** Confirm-then-delete for the selected provisional file, through the same babler chain as Data Room. */
export function useProvisionalFileDelete({ orgId, onDeleted }: { orgId: string; onDeleted: (docId: string) => void }) {
    const user = useAppSelector((state) => state.user.userData);
    const [deleteTarget, setDeleteTarget] = useState<ProvisionalFileItem | null>(null);
    const [deleting, setDeleting] = useState(false);
    const deletingRef = useRef(false);

    const confirmDelete = async (onStart?: Function, onSettle?: Function) => {
        // The Button's loading prop is CSS-only, so double-submit is guarded here.
        if (!deleteTarget || deletingRef.current) return;
        deletingRef.current = true;
        setDeleting(true);
        onStart?.();
        const docId = String(deleteTarget.docId);
        let ok = false;
        await deleteDoc(
            {
                docIds: [docId],
                requestId: getTimeStamp(),
                orgId,
                pnoId: user?.investorOrgId,
                docType: FINANCIALS_FILE_TYPE.PROVISIONAL,
                archive: false,
            },
            {
                onSuccess: () => {
                    ok = true;
                },
            },
        );
        // Always: the popup's spinner resets only through this callback.
        onSettle?.();
        deletingRef.current = false;
        setDeleting(false);
        // On failure the popup stays open for retry or cancel; deleteDoc has already toasted.
        if (!ok) return;
        toast.success(DELETE_SUCCESS_MESSAGE);
        setDeleteTarget(null);
        onDeleted(docId);
    };

    const cancelDelete = () => {
        if (!deletingRef.current) setDeleteTarget(null);
    };

    return { deleteTarget, deleting, openDelete: setDeleteTarget, confirmDelete, cancelDelete };
}
